import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ReservationScheduler.css";
import { formatLong, minsToTime, timeToMins } from "./Utilities";
import {
  MdCheckCircle,
  MdOutlineThumbUp,
  MdHourglassTop,
  MdRestaurant,
  MdCancel,
  MdErrorOutline,
  MdInfo,
  MdWarningAmber,
  MdEditCalendar,
} from "react-icons/md";
import { FaStoreSlash } from "react-icons/fa";
import * as Io5 from "react-icons/io5";
import * as Io from "react-icons/io";
import { AppContext } from "../../../context/AppContext";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import Utils from "../../../utils/Utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  timeToMinutes,
  to12Hour,
  toKey,
  toNumIfNumeric,
  ymd,
} from "../scheduler2/SchedulerHelpers";
import {
  STATUS_LIST,
  getStatusColour,
} from "../../../constants/ReservationStatus";

/* ---- Status icons ---- */
// const STATUS_ICON_MAP = {
//   Seated: <MdRestaurant />,
//   Completed: <MdCheckCircle />,
//   Approved: <MdOutlineThumbUp />,
//   Requested: <MdHourglassTop />,
//   Cancelled: <MdCancel />,
// };
const STATUS_ICON_MAP = {
  Seated: MdRestaurant,
  Completed: MdCheckCircle,
  Approved: MdOutlineThumbUp,
  Requested: MdHourglassTop,
  Cancelled: MdCancel,
  Rejected: FaStoreSlash,
  Modified: MdEditCalendar,
};
const ALERT_ICON_MAP = {
  success: MdCheckCircle,
  error: MdErrorOutline,
  info: MdInfo,
  warning: MdWarningAmber,
};

const rescheduleExcludeStatus = [
  "Seated",
  "Completed",
  "Cancelled",
  "Rejected",
];

const isTodayOrFuture = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date > today;
};

export default function EditorModal({
  payload = {},
  onClose,
  fetchDateEvents,
}) {
  const containerRef = useRef(null);
  const {
    fetchAvailableTables,
    reservationSettings,
    updateEnquiry,
    addNewReservartion,
    upcomingwithtodaysReservationList,
    fetchNewReservations,
    fetchFutureReservations,
    todaysReservationList,
    tokenData,
    tableLists,
    reservFoodpageTiming,
    closureList,
    foodpageReservSettings,
    reservationLoading,
  } = useContext(AppContext);

  const [reservationDetails, setReservationDetails] = useState(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState(payload.start ?? 0);
  const [end, setEnd] = useState(payload.end ?? 60);
  const [status, setStatus] = useState(payload.status || "Requested");
  const [tableOptionsCache, setTableOptionsCache] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [selectedTables, setSelectedTables] = useState(null);
  const [timeIntervals, setTimeIntervals] = useState(null);
  const [tableError, setTableError] = useState(false);
  const [reschedule, setReschedule] = useState(false);
  const [openCalander, setOpenCalander] = useState(false);
  const [existingTables, setExistingTable] = useState(null);

  const bookingKey = `${reservationDetails?.bookingDate || ""}|${
    reservationDetails?.bookingTime || ""
  }`;

  const showAlert = (type, message, postion = "normal") =>
    setAlert({ type, message, postion });

  const clearAlert = () => setAlert(null);

  function ensurePortalRoot() {
    let el = document.getElementById("rbm-portal-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "rbm-portal-root";
      document.body.appendChild(el);
    }
    return el;
  }

  useEffect(() => {
    if (!payload || typeof payload !== "object") return;

    const { tables, bookingTime } = payload;
    const bookingTimeNew = normaliseBookingTime(bookingTime);

    const startFromPayload = Number(payload.start);
    const startMins = Number.isFinite(startFromPayload)
      ? startFromPayload
      : bookingTimeNew && bookingTimeNew.includes(":")
      ? timeToMins(bookingTimeNew)
      : 0;

    setStart(startMins);
    setReservationDetails({
      ...payload,
      bookingTime: bookingTimeNew,
      status: payload?.status || "Requested",
      message: payload?.message ?? "",
    });

    const parsedTables =
      tables && tables.length != 0 && typeof tables == "string"
        ? JSON.parse(tables)
        : tables;

    setExistingTable(parsedTables);

    if (reschedule) getReservTiming(payload?.date);
  }, [payload]);

  useEffect(() => {
    if (!payload || !payload?.tables || !tableOptionsCache) return;

    const parsedTables =
      typeof payload?.tables == "string"
        ? JSON.parse(payload?.tables)
        : payload?.tables;

    if (
      !parsedTables ||
      !Array.isArray(parsedTables) ||
      parsedTables?.length === 0
    )
      return;
    const isValid = allParsedExistInTables(parsedTables, tableOptionsCache);

    if (!isValid) {
      showAlert("error", "Selected Table is not currenlty available!");
    } else {
      setIsError(null);
    }
  }, [payload?.tables, tableLists]);

  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const min = getTodayString();

  useEffect(() => {
    containerRef.current = ensurePortalRoot();
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const mergeDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const t = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    return `${dateStr} ${t}`;
  };

  const getClosureWindowsForDate = (date) => {
    if (!Array.isArray(closureList) || closureList.length === 0) return [];

    const dateObj = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObj.getTime())) return [];

    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const targetDateStr = `${dateObj.getFullYear()}-${pad(
      dateObj.getMonth() + 1
    )}-${pad(dateObj.getDate())}`;

    return closureList
      .filter((c) => c.status === "Active")
      .map((c) => {
        const startDateStr = c.startTime.slice(0, 10);
        const endDateStr = c.endTime.slice(0, 10);
        const startTimeStr = c.startTime.slice(11, 16);
        const endTimeStr = c.endTime.slice(11, 16);

        if (targetDateStr < startDateStr || targetDateStr > endDateStr) {
          return null;
        }

        let dayStart;
        let dayEnd;

        if (startDateStr === endDateStr) {
          // single-day closure
          dayStart = startTimeStr;
          dayEnd = endTimeStr;
        } else if (targetDateStr === startDateStr) {
          // first day of multi-day closure
          dayStart = startTimeStr;
          dayEnd = "23:59";
        } else if (targetDateStr === endDateStr) {
          // last day of multi-day closure
          dayStart = "00:00";
          dayEnd = endTimeStr;
        } else {
          // middle day of multi-day closure
          dayStart = "00:00";
          dayEnd = "23:59";
        }

        return { startTime: dayStart, endTime: dayEnd };
      })
      .filter(Boolean);
  };
  useEffect(() => {
    console.log("tables", selectedTables);
  }, [selectedTables]);

  const getReservTiming = (date) => {
    if (!reservFoodpageTiming || !date) return;

    const parsedDate = ymd(date);

    const isDateChanged =
      reservationDetails?.bookingDate &&
      reservationDetails.bookingDate !== parsedDate;

    setReservationDetails((prev) => ({
      ...prev,
      date: parsedDate,
      bookingDate: parsedDate,
      bookingTime: isDateChanged ? "" : prev.bookingTime,
      tables: "",
    }));

    if (isDateChanged) {
      setSelectedTables(null);
      setReservationDetails((prev) => ({ ...prev, tables: null }));
      setExistingTable(null);
      setTableOptionsCache([]);
    }

    const dayValue = Utils.getDayOfWeek(parsedDate);
    const todaysTiming = reservFoodpageTiming[dayValue];

    const intervalDuration =
      typeof foodpageReservSettings?.timeinterval === "string"
        ? parseInt(foodpageReservSettings?.timeinterval, 10)
        : 30;

    if (!todaysTiming || todaysTiming.length === 0) {
      setTimeIntervals([]);
      return;
    }

    //! Generate all intervals from opening/closing time
    const allIntervals = todaysTiming.flatMap((time) => {
      return Utils.getReservIntervals(
        time.openingTime,
        time.closingTime,
        intervalDuration
      );
    });

    //! Get closure window(s) for this date
    const closureWindows = getClosureWindowsForDate(date, closureList);

    if (!closureWindows || closureWindows.length === 0) {
      setTimeIntervals(allIntervals);
      setOpenCalander(false);
      return;
    }

    //! Filter out intervals that fall inside any closure window
    const filteredIntervals = allIntervals.filter((time) => {
      return closureWindows.every((win) => {
        const start = win.startTime;
        const end = win.endTime;
        return time < start || time > end;
      });
    });

    setTimeIntervals(filteredIntervals);
    setOpenCalander(false);
  };

  useEffect(() => {
    if (!reservationDetails?.bookingDate && reschedule) return;
    getReservTiming(reservationDetails?.bookingDate);
  }, [reservationDetails?.bookingDate, reschedule]);

  //! validate reservation within open time & closure list
  const isReservationWithinOpenTime = (
    reservFoodpageTiming,
    reservationDetails
  ) => {
    // basic input validation
    if (
      !reservFoodpageTiming ||
      !reservationDetails?.bookingDate ||
      !reservationDetails?.bookingTime
    ) {
      return {
        isValid: false,
        errorText: "Booking date and time are required.",
      };
    }

    const bookingDateObj = new Date(reservationDetails.bookingDate);
    if (isNaN(bookingDateObj.getTime())) {
      console.error("Invalid bookingDate:", reservationDetails.bookingDate);
      return {
        isValid: false,
        errorText: "Invalid booking date.",
      };
    }

    // build full booking datetime
    const timeStr = reservationDetails.bookingTime;
    const normalisedTime = timeStr?.length === 5 ? `${timeStr}:00` : timeStr;

    const bookingDateTime = new Date(
      `${reservationDetails.bookingDate} ${normalisedTime}`.replace(" ", "T")
    );
    if (isNaN(bookingDateTime.getTime())) {
      console.error(
        "Invalid booking datetime:",
        reservationDetails.bookingDate,
        reservationDetails.bookingTime
      );
      return {
        isValid: false,
        errorText: "Invalid booking time.",
      };
    }

    // check closureList (if any) – assumed from outer scope
    if (Array.isArray(closureList) && closureList.length > 0) {
      const matchingClosure = closureList.find((c) => {
        if (c.status !== "Active") return false;

        const start = new Date(c.startTime.replace(" ", "T"));
        const end = new Date(c.endTime.replace(" ", "T"));

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

        return bookingDateTime >= start && bookingDateTime <= end;
      });

      if (matchingClosure) {
        const startTime24 = matchingClosure.startTime.slice(11, 16);
        const endTime24 = matchingClosure.endTime.slice(11, 16);
        const startTime12 = to12Hour(startTime24);
        const endTime12 = to12Hour(endTime24);
        const dateStr = matchingClosure.startTime.slice(0, 10);

        return {
          isValid: false,
          errorText: `Closed on ${dateStr} from ${startTime12} to ${endTime12}.`,
        };
      }
    }

    // validate against regular opening times
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const dayKey = dayNames[bookingDateObj.getDay()];

    const daySlots = reservFoodpageTiming[dayKey];
    if (!Array.isArray(daySlots) || daySlots.length === 0) {
      return {
        isValid: false,
        errorText: "Shop is closed on the selected day.",
      };
    }

    const bookingMinutes = timeToMinutes(reservationDetails.bookingTime);
    if (bookingMinutes == null) {
      console.error("Invalid bookingTime:", reservationDetails.bookingTime);
      return {
        isValid: false,
        errorText: "Invalid booking time.",
      };
    }

    const isWithinOpenTime = daySlots
      .filter((slot) => slot.status === "active")
      .some((slot) => {
        const openMin = timeToMinutes(slot.openingTime);
        const closeMin = timeToMinutes(slot.closingTime);
        if (openMin == null || closeMin == null) return false;

        return bookingMinutes >= openMin && bookingMinutes <= closeMin;
      });

    if (!isWithinOpenTime) {
      return {
        isValid: false,
        errorText: "Selected time is outside opening hours.",
      };
    }

    return {
      isValid: true,
      errorText: null,
    };
  };

  const fetchCurrentTable = async () => {
    if (!tableLists || tableLists.length == 0 || !reservationDetails?.tableId)
      return;

    if (
      existingTables &&
      Array.isArray(existingTables) &&
      existingTables.length != 0
    ) {
      const data = tableLists.filter((x) => {
        return existingTables.find((y) => y == x.id);
      });

      const alteredData = data.map((d) => {
        return {
          value: toNumIfNumeric(d?.id),
          label: `${d?.name || "Table"}${d?.chairs ? `  (${d.chairs})` : ""}`,
          meta: {
            id: d?.id,
            shopID: d?.shopID,
            tableID: d?.tableID,
            name: d?.name,
            chairs: d?.chairs,
            status: d?.status,
            tableType: d?.tableType,
          },
        };
      });
      setSelectedTables(alteredData);
      return;
    }

    await loadTableOptions();
    const value = tableLists.find((x) => x.id === reservationDetails?.tableId);
    if (!value) return;

    const option = {
      value: toNumIfNumeric(value?.id),
      label: `${value?.name || "Table"}${
        value?.chairs ? `  (${value.chairs})` : ""
      }`,
      meta: {
        id: value?.id,
        shopID: value?.shopID,
        tableID: value?.tableID,
        name: value?.name,
        chairs: value?.chairs,
        status: value?.status,
        tableType: value?.tableType,
      },
    };

    setSelectedTables([option]);
    setReservationDetails((prev) => ({ ...prev, tables: [option] }));
  };

  const isPastReservation = (details) => {
    if (!details?.bookingDate || !details?.bookingTime) return false;

    let timeStr = details.bookingTime;

    if (timeStr.includes(" ")) {
      const [datePart, timePart] = timeStr.split(" ");
      const isoString = `${datePart}T${timePart}`;
      const bookingDateTime = new Date(isoString);
      return bookingDateTime < new Date();
    }

    const isoString = `${details.bookingDate}T${details.bookingTime}`;
    const bookingDateTime = new Date(isoString);

    return bookingDateTime < new Date();
  };

  useEffect(() => {
    if (reschedule) return;
    fetchCurrentTable();
  }, [existingTables]);

  // useEffect(() => {
  //   if (!reservationDetails) return;

  //   setSelectedTables([]);
  //   setTableOptionsCache([]);
  //   setReservationDetails((prev) => ({
  //     ...prev,
  //     tables: [],
  //     tableId: "",
  //   }));
  // }, [bookingKey]);

  // const getAvailableTabesLists = async () => {
  //   if (!reservationDetails) return;

  //   const mergedDateTime = {
  //     bookingTime:
  //       reservationDetails?.bookingDate + " " + reservationDetails?.bookingTime,
  //   };

  //   if (!mergedDateTime?.bookingTime) return;

  //   await fetchAvailableTables(mergedDateTime, {
  //     onSuccess: (res) => {
  //       const resp = res?.data;
  //       if (resp && resp.available) {
  //         setAvailableTables(resp?.available);
  //       }
  //     },
  //     onFailed: (err) => {
  //       console.error("Error on nfetching data", err);
  //     },
  //   });
  // };

  const loadTableOptions = async (inputValue) => {
    const merged = mergeDateTime(
      reservationDetails?.bookingDate,
      reservationDetails?.bookingTime
    );

    if (!merged) return [];

    try {
      setTablesLoading(true);

      const res = await new Promise((resolve, reject) => {
        fetchAvailableTables(
          { bookingTime: merged, q: inputValue || "" },
          { onSuccess: resolve, onFailed: reject }
        );
      });

      const list = Array.isArray(res?.data?.available)
        ? res.data.available
        : [];

      let opts = list.map((t) => {
        const rawId = t?.id;
        const value = toNumIfNumeric(rawId);
        return {
          value,
          label: `${t.name || t.label || "Table"}${
            t.chairs ? `  (${t.chairs})` : ""
          }`,
          meta: t,
        };
      });

      // Ensure preselected tables exist in options too
      if (selectedTables && selectedTables.length) {
        const existingIds = new Set(
          opts.map((o) => String(o.meta?.id ?? o.value))
        );

        const extraFromSelected = selectedTables.filter((sel) => {
          const id = String(sel.meta?.id ?? sel.value);
          return !existingIds.has(id);
        });

        if (extraFromSelected.length) {
          opts = [...opts, ...extraFromSelected];
        }
      }

      setTableOptionsCache(opts);

      if (!inputValue) return opts;

      const q = inputValue.toLowerCase();
      return opts.filter((o) => o.label.toLowerCase().includes(q));
    } catch (e) {
      console.error("fetchAvailableTables failed", e);
      return [];
    } finally {
      setTablesLoading(false);
    }
  };

  const handleTablesMenuOpen = async () => {
    if (tableOptionsCache.length === 0) {
      await loadTableOptions("");
    }
  };

  // const selectedTableOptions = (reservationDetails?.tables || []).map((id) => {
  //   const found = tableOptionsCache.find((o) => {
  //     const ovn = Number(o.value);
  //     if (Number.isFinite(ovn) && !Number.isNaN(ovn)) return ovn === id;
  //     return String(o.value) === String(id);
  //   });
  //   return found || { value: id, label: String(id) };
  // });

  //! When user selects, store ids and first tableId
  const handleTablesChange = (values, actionMeta) => {
    const safeValues = values || [];

    const ids = safeValues.map((v) => {
      const n = Number(v.value);
      return Number.isFinite(n) && !Number.isNaN(n) ? n : v.value;
    });

    setSelectedTables(safeValues);

    setReservationDetails((p) => ({
      ...p,
      tables: ids,
      tableId: ids[0] || "",
    }));

    if (
      actionMeta?.action === "remove-value" ||
      actionMeta?.action === "pop-value"
    ) {
      const removed = actionMeta.removedValue;
      if (!removed) return;

      setTableOptionsCache((prev) => {
        const removedId = String(removed.meta?.id ?? removed.value);

        const exists = prev.some(
          (opt) => String(opt.meta?.id ?? opt.value) === removedId
        );
        if (exists) return prev;

        return [...prev, removed];
      });

      return;
    }

    if (actionMeta?.action === "clear") {
      //! existig tables setup
      // const hasExistingTables =
      //   Array.isArray(existingTables) && existingTables.length > 0;

      // if (hasExistingTables) {
      loadTableOptions("");
      // }

      return;
    }
  };

  const validateChairsandPeoples = () => {
    if (
      !selectedTables ||
      !reservationDetails?.chairs ||
      reservationDetails?.chairs == 0
    )
      return;

    const totalChairs = selectedTables.reduce(
      (sum, o) => sum + (parseInt(o.meta?.chairs, 10) || 0),
      0
    );

    //  if (Number(reservationDetails?.chairs) < totalChairs) {
    //   showAlert(
    //     "warning",
    //     "The selected table(s) are too large for the number of people"
    //   );
    //   return true;
    // } else

    if (reservationDetails?.chairs == 0) {
      showAlert("error", "Please choose tables!");
      setTableError(true);
    }

    if (Number(reservationDetails?.chairs) > totalChairs) {
      showAlert(
        "error",
        "The selected table(s) are too small for the number of people"
      );
      setTableError(true);
      return true;
    } else {
      clearAlert();
      setTableError(false);
      return false;
    }
  };

  useEffect(() => {
    validateChairsandPeoples();
  }, [selectedTables, reservationDetails?.chairs]);

  const normaliseBookingTime = (raw) => {
    if (!raw || typeof raw !== "string") return "";

    const trimmed = raw.trim();

    const dateTimeMatch = trimmed.match(
      /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})(:\d{2})?$/
    );
    if (dateTimeMatch) {
      return dateTimeMatch[2];
    }

    const timeMatch = trimmed.match(/^(\d{2}:\d{2})(:\d{2})?$/);
    if (timeMatch) {
      return timeMatch[1];
    }

    return "";
  };

  function allParsedExistInTables(parsedTables, tableLists) {
    const existingIds = new Set(tableLists.map((t) => String(t.id)));
    return parsedTables.every((x) => existingIds.has(String(x)));
  }

  const statusStyle = useMemo(() => {
    const s = reservationDetails?.status || status;
    const c = getStatusColour(s);
    // Match scheduler chips / enquiry badges: accent as fill, surface as text
    return {
      backgroundColor: c.text,
      color: c.bg,
      borderColor: c.text,
    };
  }, [reservationDetails?.status, status]);

  // Same rules as scheduler chip ⋮ PortalMenu
  const statusOptions = useMemo(() => {
    const current = reservationDetails?.status || status;
    const isFutureBooking = isTodayOrFuture(reservationDetails?.bookingDate);

    return STATUS_LIST.filter((label) => {
      if (label === "Modified") return false;
      if (
        isFutureBooking &&
        (label === "Seated" || label === "Completed") &&
        label !== current
      ) {
        return false;
      }
      return true;
    });
  }, [reservationDetails?.bookingDate, reservationDetails?.status, status]);

  const setField = (name, value) =>
    setReservationDetails((p) => ({ ...p, [name]: value }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const regex = /^[0-9\b]+$/;
      if (value === "" || regex.test(value)) setField(name, value);
      return;
    }
    setReservationDetails((prev) => ({ ...prev, [name]: value }));
  };

  const incDec = (name, delta, clamp = [1, 50]) => {
    setReservationDetails((p) => {
      const now = Number(p?.[name] ?? 0);
      const next = Math.max(clamp[0], Math.min(clamp[1], now + delta));
      return { ...p, [name]: next };
    });
  };

  const validateFields = () => {
    const r = reservationDetails || {};
    const missing =
      !r.name ||
      !r.phone ||
      !r.email ||
      !r.chairs ||
      !r.bookingDate ||
      !r.bookingTime;
    setIsError(missing);
    return missing;
  };

  const isTodayPastTime = () => {
    const r = reservationDetails || {};
    if (!r.bookingDate || !r.bookingTime) return false;
    const now = new Date();
    const dt = new Date(
      r.bookingDate +
        "T" +
        (r.bookingTime.length === 5 ? r.bookingTime + ":00" : r.bookingTime)
    );
    const sameDay =
      now.getFullYear() === dt.getFullYear() &&
      now.getMonth() === dt.getMonth() &&
      now.getDate() === dt.getDate();
    return sameDay && dt < now;
  };

  const isArrayOfNumbers = (arr) =>
    Array.isArray(arr) && arr.every((x) => typeof x === "number");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const valid = validateFields();
    const validateTablesAndChairs = validateChairsandPeoples();
    if (valid) return;
    if (validateTablesAndChairs) return;
    try {
      setLoading(true);
      const max = Number(reservationSettings?.max_party_size ?? 0);
      if (!max) {
        showAlert("error", "Reservation settings not available.");
        return;
      }
      if (Number(reservationDetails?.chairs) <= 0) {
        showAlert("error", "Please choose a minimum party size.");
        return;
      }
      if (Number(reservationDetails?.chairs) > max) {
        showAlert("error", `Party size must be less than or equal to ${max}.`);
        return;
      }

      //! past time
      // if (isTodayPastTime()) {
      //   showAlert("error", "Selected time is in the past.");
      //   return;
      // }

      const { errorText, isValid } = isReservationWithinOpenTime(
        reservFoodpageTiming,
        reservationDetails
      );

      if (!isValid) {
        showAlert("error", errorText);
        return;
      }

      if (
        rescheduleExcludeStatus.includes(reservationDetails?.status) &&
        reschedule
      ) {
        showAlert(
          "error",
          `${reservationDetails?.status} status cannot be rescheduled!`
        );
        return;
      }

      const {
        id,
        bookingDate,
        bookingTime,
        start: _s,
        end: _e,
        date,
        tableId,
        tables,
        shopMessage,
        ...rest
      } = reservationDetails || {};

      //! validate upcoming date with status
      const isFuture = isTodayOrFuture(bookingDate);
      console.log(isFuture, "isFuture");

      if (!!isFuture && (status === "Completed" || status === "Seated")) {
        showAlert(
          "warning",
          `This action is not allowed: future reservations cannot be updated to ${status}.`
        );
        return;
      }

      // const errTable = tables && tables.length != 0 && tables.filter(x => x.value)

      const hasExistingTables =
        Array.isArray(existingTables) && existingTables.length > 0;

      const hasCurrentTables =
        !!tables &&
        (!Array.isArray(tables) ||
          (Array.isArray(tables) && tables.length > 0));

      if (!hasCurrentTables && !hasExistingTables) {
        showAlert(
          "error",
          "Couldn't get the table details, Please choose table again!"
        );
        return;
      }

      // Prefer tables from state; if missing/empty, fall back to existingTables
      const rawTables = hasCurrentTables ? tables : existingTables;

      const timePart =
        bookingTime.length === 5 ? `${bookingTime}:00` : bookingTime;
      const siteStorageURL = localStorage.getItem("siteURL") || "";
      const siteURL = siteStorageURL
        ? `${siteStorageURL}/view-reservation?reserv=`
        : "";

      const parsedTables =
        rawTables && rawTables?.length != 0 && typeof rawTables === "string"
          ? JSON.parse(rawTables)
          : rawTables;

      const isValidTables =
        parsedTables && Array.isArray(parsedTables) && parsedTables.length != 0
          ? isArrayOfNumbers(parsedTables)
          : null;

      let normalizedTables = parsedTables ?? null;

      if (!isValidTables) {
        const data = parsedTables
          .map((item) => {
            const id = item.id ?? item.value ?? item.tableID ?? item.meta?.id;

            return Number(id);
          })
          .filter((n) => !Number.isNaN(n));

        normalizedTables = data;
      }

      const shopToken = tokenData?.shopCustomToken;
      if (!shopToken) return showAlert("error", "Authorisation token missing.");
      const headers = { "x-shopToken": shopToken };
      const bookingMerged = `${bookingDate} ${timePart}`;

      if (!id || id === 0 || id === "0") {
        const createData = {
          ...rest,
          tables: normalizedTables,
          bookingTime: bookingMerged,
          chairs: Number(reservationDetails?.chairs) || 1,
          userID: 0,
          advancePayment: "",
          advanceAmount: "",
          amountStatus: "",
          paymentMethod: "",
          transactionID: "",
          shopmessage: "",
          baseUrl: siteURL,
          source: "nextJs",
        };

        await addNewReservartion?.(createData, {
          onSuccess: async () => {
            showAlert("success", "Reservation created successfully.");
            await fetchFutureReservations();
            await todaysReservationList();
            await upcomingwithtodaysReservationList();
            fetchDateEvents();
            setTimeout(() => {
              onClose?.();
            }, 1200);
          },
          onFailed: (err) => {
            console.error("Error on creating new reservation", err);
            showAlert(
              "error",
              err?.messages ?? "Creation failed. Please try again."
            );
          },
          headers,
        });
        return;
      }

      delete rest?.formattedID;
      delete rest?.addedTime;

      const updatePayload = {
        ...rest,
        tables: normalizedTables,
        bookingDate,
        bookingTime: timePart,
        chairs: Number(reservationDetails?.chairs) || 1,
        userID: 0,
        advancePayment: "",
        advanceAmount: "",
        amountStatus: "",
        paymentMethod: "",
        transactionID: "",
        shopmessage: "",
        baseUrl: siteURL,
        source: "NextJs",
        isReScheduled: reschedule ? "Yes" : null,
      };

      if (
        !updatePayload?.tables ||
        !Array.isArray(updatePayload?.tables) ||
        updatePayload?.tables.length == 0
      ) {
        showAlert("error", "Please select the tables!");
        return;
      }

      await updateEnquiry?.(id, updatePayload, {
        onSuccess: async () => {
          showAlert("success", "Reservation details updated successfully.");

          // await todaysReservationList();
          // await upcomingwithtodaysReservationList();
          fetchDateEvents();
          setTimeout(() => {
            // onClose?.();
            window.location.reload();
          }, 1200);
        },
        onFailed: (err) =>
          showAlert(
            "error",
            err?.messages ?? "Update failed. Please try again."
          ),
        headers,
      });
    } catch (err) {
      console.error(err);
      showAlert("error", "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  if (!containerRef.current) return null;

  return createPortal(
    <div className="rbm-overlay" aria-modal="true" role="dialog">
      <div className="rbm-modal rbm-modal--fullscreen rbm-light">
        <div className="rbm-header">
          <div className="rbm-title position-relative">
            <small style={{ color: "#747474" }}>
              {reservationDetails?.formattedID
                ? `#${reservationDetails?.formattedID} `
                : "New Reservation"}{" "}
            </small>{" "}
            <br />
            {!reschedule ? (
              <h4>{formatLong(payload?.date)}</h4>
            ) : (
              <h4>Reschedule</h4>
            )}
          </div>
          <button className="rbm-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <form className="rbm-body" onSubmit={handleSubmit}>
          {/* LEFT */}
          <div className="rbm-col rbm-col--left">
            {/* Row: Date / Time / People */}
            {reschedule && (
              <div className="rbm-row">
                <div className="rbm-field rbm-field--grow">
                  <label className="rbm-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name=""
                    id=""
                    value={reservationDetails?.date}
                    onChange={(e) => getReservTiming(e.target.value)}
                    min={min}
                  />
                </div>
              </div>
            )}
            <div className="rbm-row">
              <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">Time</label>

                {/* <select
                  name="bookingTime"
                  id=""
                  className="form-select"
                  onChange={(e) => {
                    const v = e.target.value;
                    const s = timeToMins(v);
                    const eM = s + (end - start || 60);
                    setField("bookingTime", v);
                    setStart(s);
                    setEnd(eM);
                    setSelectedTables([]);
                    setTableOptionsCache([]);
                    setReservationDetails((prev) => ({
                      ...prev,
                      bookingTime: e.target.value,
                      tableId: null,
                      tables: [],
                    }));
                  }}
                >
                  <option value="">Choose booking time</option>
                  {timeIntervals &&
                    timeIntervals.length != 0 &&
                    timeIntervals.map((interval, index) => {
                      return (
                        <option value={interval} key={index}>
                          {interval ? Utils.convertTiming(interval) : "N/A"}
                        </option>
                      );
                    })}
                </select> */}
                {!reschedule ? (
                  <input
                    className="rbm-input"
                    type="time"
                    step={900}
                    name="bookingTime"
                    value={minsToTime(start)}
                    onChange={(e) => {
                      const v = e.target.value;
                      const s = timeToMins(v);
                      const eM = s + (end - start || 60);
                      setField("bookingTime", v);
                      setStart(s);
                      setEnd(eM);
                      setSelectedTables([]);
                      setTableOptionsCache([]);
                      setReservationDetails((prev) => ({
                        ...prev,
                        bookingTime: e.target.value,
                        tableId: null,
                        tables: [],
                      }));
                    }}
                    // disabled={!!reservationDetails?.id}
                  />
                ) : (
                  <select
                    name="bookingTime"
                    id=""
                    className="form-select"
                    onChange={handleChange}
                    value={
                      reservationDetails?.bookingTime
                        ? reservationDetails.bookingTime.slice(0, 5)
                        : ""
                    }
                  >
                    <option value="">Choose booking time</option>
                    {timeIntervals &&
                      timeIntervals.length != 0 &&
                      timeIntervals.map((interval, index) => {
                        return (
                          <option value={interval} key={index}>
                            {interval ? Utils.convertTiming(interval) : "N/A"}
                          </option>
                        );
                      })}
                  </select>
                )}
                {isError && !reservationDetails?.bookingTime && (
                  <small className="rbm-err">Time is required</small>
                )}
              </div>

              <div className="rbm-field rbm-field--compact">
                <label className="rbm-label">People</label>
                <div className="rbm-stepper">
                  <button
                    type="button"
                    className="rbm-step"
                    onClick={() => incDec("chairs", -1)}
                  >
                    −
                  </button>
                  <input
                    className="rbm-input rbm-input--center"
                    name="chairs"
                    value={reservationDetails?.chairs ?? 2}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="rbm-step"
                    onClick={() => incDec("chairs", +1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Row: Name */}
            <div className="rbm-row rbm-row--single">
              <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">Table (multi-select)</label>
                <AsyncSelect
                  key={bookingKey}
                  cacheOptions
                  defaultOptions={
                    tableOptionsCache.length ? tableOptionsCache : false
                  }
                  loadOptions={loadTableOptions}
                  onMenuOpen={handleTablesMenuOpen}
                  isMulti
                  isClearable
                  isLoading={tablesLoading}
                  value={selectedTables}
                  onChange={handleTablesChange}
                  placeholder={
                    reservationDetails?.bookingDate &&
                    reservationDetails?.bookingTime
                      ? "Select available tables…"
                      : "Pick date & time first"
                  }
                  // Small UX polish to match your light UI
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 42,
                      borderRadius: 10,
                      borderColor: "#E5E7EB",
                      boxShadow: "none",
                      ":hover": { borderColor: "#CBD5E1" },
                      fontFamily: "Inter, sans-serif",
                      fontSize: 14,
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  isDisabled={reservationLoading}
                  onInputChange={(input) => input}
                  classNamePrefix={tableError ? "error" : ""}
                  noOptionsMessage={() => "No tables available for this time."}
                />
                {/* {alert?.message && alert.postion == "table" && (
                  <div
                    className={`rbm-alert rbm-alert--${alert.type}`}
                    role="alert"
                    style={{ margin: "12px 0", padding: "5px" }}
                  >
                    {(() => {
                      const Icon = ALERT_ICON_MAP[alert.type] || MdInfo;
                      return (
                        <Icon className="rbm-alert__icon" aria-hidden="true" />
                      );
                    })()}
                    {alert.message}
                    <button
                      type="button"
                      className="rbm-alert__close"
                      aria-label="Dismiss"
                      onClick={clearAlert}
                    >
                      ×
                    </button>
                  </div>
                )} */}
              </div>
            </div>

            <div className="rbm-row">
              <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">Name</label>
                <input
                  className="rbm-input"
                  name="name"
                  type="text"
                  value={reservationDetails?.name || ""}
                  placeholder="(or find by name, phone, email)"
                  onChange={handleChange}
                />
                {isError && !reservationDetails?.name && (
                  <small className="rbm-err">Name is required</small>
                )}
              </div>

              <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">E-mail</label>
                <input
                  className="rbm-input"
                  name="email"
                  type="email"
                  value={reservationDetails?.email || ""}
                  onChange={handleChange}
                  placeholder="Email address"
                />
                {isError && !reservationDetails?.email && (
                  <small className="rbm-err">Email is required</small>
                )}
              </div>
            </div>

            {/* Row: Table (multi) / Duration */}
            <div className="rbm-row">
              <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">Phone</label>
                <input
                  className="rbm-input"
                  name="phone"
                  value={reservationDetails?.phone || ""}
                  onChange={handleChange}
                  placeholder="Phone number"
                />
                {isError && !reservationDetails?.phone && (
                  <small className="rbm-err">Phone number is required</small>
                )}
              </div>
              {/* <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">Duration</label>
                <div className="rbm-stepper">
                  <button
                    type="button"
                    className="rbm-step"
                    onClick={() => {
                      const next = Math.max(30, end - start - 15);
                      setEnd(start + next);
                    }}
                  >
                    −
                  </button>
                  <input
                    className="rbm-input rbm-input--center"
                    readOnly
                    value={`${Math.max(0, end - start)} mins`}
                  />
                  <button
                    type="button"
                    className="rbm-step"
                    onClick={() => setEnd(end + 15)}
                  >
                    +
                  </button>
                </div>
              </div> */}
              <div className="rbm-field rbm-field--grow">
                <label className="rbm-label">Status</label>

                {/* Wrapper ensures the icon sits on top of the native select */}
                <div className="rbm-status-wrap" style={statusStyle}>
                  {(() => {
                    const S =
                      STATUS_ICON_MAP[reservationDetails?.status || status] ||
                      MdHourglassTop;
                    return <S className="rbm-status-ico" aria-hidden="true" />;
                  })()}
                  <select
                    className="rbm-input rbm-status-select rbm-status-select--withIcon"
                    name="status"
                    value={reservationDetails?.status || status}
                    style={statusStyle}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      handleChange(e);
                    }}
                  >
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    {reservationDetails?.status === "Modified" && (
                      <option value="Modified" disabled>
                        Modified
                      </option>
                    )}
                  </select>
                  <span className="down_ico">
                    <Io5.IoChevronDownOutline />
                  </span>
                </div>
                {/* Status pill mirrors selection */}
                {/* <div
                className="rbm-status-pill"
                style={{
                  background: (
                    STATUS_COLOURS[reservationDetails?.status || status] || {}
                  ).bg,
                  color: (
                    STATUS_COLOURS[reservationDetails?.status || status] || {}
                  ).text,
                }}
              >
                {(() => {
                  const S =
                    STATUS_ICON_MAP[reservationDetails?.status || status] ||
                    MdHourglassTop;
                  return <S style={{ marginRight: 6 }} aria-hidden="true" />;
                })()}
                {reservationDetails?.status || status}
              </div> */}
              </div>
            </div>

            {/* Row: Status (icons/colors) / Source */}
          </div>

          {/* RIGHT */}
          <div className="rbm-col rbm-col--right">
            <div className="rbm-field">
              <label className="rbm-label">Message to guest</label>
              <textarea
                className="rbm-input rbm-textarea"
                rows={8}
                name="message"
                value={reservationDetails?.message || ""}
                onChange={handleChange}
              />
            </div>

            {/* <div className="rbm-field">
              <label className="rbm-label">Private internal note</label>
              <textarea
                className="rbm-input rbm-textarea"
                rows={8}
                name="message"
                value={reservationDetails?.message || ""}
                onChange={handleChange}
              />
            </div> */}
          </div>
        </form>
        {isPastReservation(reservationDetails) && (
          <p
            className="rbm-alert rbm-alert--warning"
            role="alert"
            style={{ margin: "12px 16px 0" }}
          >
            {(() => {
              const Icon = ALERT_ICON_MAP["warning"] || MdInfo;
              return <Icon className="rbm-alert__icon" aria-hidden="true" />;
            })()}
            Reschedule is unavailable: the selected reservation time is already
            over.
          </p>
        )}
        {alert?.message && alert.postion == "normal" && (
          <div
            className={`rbm-alert rbm-alert--${alert.type}`}
            role="alert"
            style={{ margin: "12px 16px 0" }}
          >
            {(() => {
              const Icon = ALERT_ICON_MAP[alert.type] || MdInfo;
              return <Icon className="rbm-alert__icon" aria-hidden="true" />;
            })()}
            {alert.message}
            <button
              type="button"
              className="rbm-alert__close"
              aria-label="Dismiss"
              onClick={clearAlert}
            >
              ×
            </button>
          </div>
        )}
        <br />
        <div className="rbm-footer">
          <div className="d-flex justify-content-between w-100">
            <div>
              <button
                className="rbm-btn rbm-btn--primary me-4"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Please wait…" : "Save booking"}
              </button>
              <button
                className="rbm-btn ms-4"
                onClick={() => {
                  onClose();
                  setSelectedTables([]);
                  setTableOptionsCache([]);
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>

          {reservationDetails?.id &&
            !isPastReservation(payload) &&
            !reschedule &&
            !rescheduleExcludeStatus.includes(reservationDetails?.status) && (
              <button
                className="rbm-btn rbm-btn--secondary"
                onClick={() => {
                  setReschedule(true);
                  setOpenCalander(true);
                }}
              >
                Reschedule
              </button>
            )}
        </div>
      </div>
    </div>,
    containerRef.current
  );
}
